// EvStockExcelExport.js
// Usage: import { exportEvStockToExcel } from "./EvStockExcelExport";

import * as XLSX from "xlsx";

const fmt = (v) =>
  v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const fmtPrice = (v) => (v != null ? Number(v).toLocaleString("en-IN") : "—");

const VEHICLE_LABEL = { E_RICKSHAW: "E-Rickshaw", SCOOTY: "Scooty" };

/**
 * Flatten a single EV stock bike into a flat Excel row
 */
const flattenBike = (bike, locationName) => ({
  "Location":             locationName || bike.location || "—",
  "Sl No":                bike.slNo ?? "—",
  "Entry Date":           fmt(bike.date),
  "Vehicle Type":         VEHICLE_LABEL[bike.vehicleType] || bike.vehicleType || "—",
  "Company Name":         bike.companyName || "—",
  "Model":                bike.model || "—",
  "Colour":               bike.colour || "—",
  "Engine No":            bike.engine || "—",
  "Chasis No":            bike.chasis || "—",
  "Invoice No":           bike.invoiceNo || "—",
  "HSN Code":             bike.HSN || "—",
  "Battery Type":         bike.batteryType || "—",
  "OAM Battery":          bike.oamBattery ? "Yes" : "No",
  "Controller No":        bike.controllerNo || "—",
  "Controller Warranty":  fmt(bike.controllerWarranty),
  "Motor Warranty":       fmt(bike.motorWarranty),
  "Charger":              bike.charger || "—",
  "Charger Warranty":     fmt(bike.chargerWarranty),
  "Warranty Period":      fmt(bike.warrantyPeriod),
  "Form-22 Received":     bike.form22Received ? "Yes" : "No",
  "Quantity":             bike.quantity ?? 1,
   "Sale Price (₹)":       bike.salePrice ?? 0,
  "Transport Price (₹)":  bike.transportPrice ?? 0,
  "Source":               bike.source || "—",
  "Status":               bike.status || "—",
  "Invoice Status":       bike.invoiceStatus ? "Done" : "Pending",
  "Sale Date":            fmt(bike.saleDate),
  "Remarks":              bike.remarks || "—",
  "Created At":           fmt(bike.createdAt),
});

/**
 * exportEvStockToExcel
 * @param {Array}  groupedData  - [{ location, bikes[], total }]
 * @param {Object} filters      - active filter state
 * @param {String} label        - readable label for title
 */
export const exportStockToExcel = (groupedData, filters = {}, label = "All EV Stock") => {
  const wb = XLSX.utils.book_new();

  /* ── COL WIDTHS (reused) ── */
  const colWidths = [
    { wch: 14 }, { wch: 7 },  { wch: 14 }, { wch: 12 }, { wch: 18 },
    { wch: 22 }, { wch: 12 }, { wch: 22 }, { wch: 22 }, { wch: 18 },
    { wch: 10 }, { wch: 12 }, { wch: 11 }, { wch: 18 }, { wch: 18 },
    { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 14 },
    { wch: 9 },  { wch: 18 }, { wch: 15 }, { wch: 18 }, { wch: 10 },
    { wch: 10 }, { wch: 14 }, { wch: 12 }, { wch: 22 }, { wch: 14 },
  ];

  /* ── 1. ALL BIKES SHEET ── */
  const allRows = [];
  groupedData.forEach((loc) =>
    loc.bikes.forEach((bike) => allRows.push(flattenBike(bike, loc.location)))
  );
  if (allRows.length > 0) {
    const ws = XLSX.utils.json_to_sheet(allRows);
    ws["!cols"] = colWidths;
    XLSX.utils.book_append_sheet(wb, ws, "All Stock");
  }

  /* ── 2. PER-LOCATION SHEETS ── */
  groupedData.forEach((loc) => {
    const rows = loc.bikes.map((b) => flattenBike(b, loc.location));
    if (!rows.length) return;
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = colWidths;
    XLSX.utils.book_append_sheet(wb, ws, (loc.location || "Unknown").slice(0, 31));
  });

  /* ── 3. SUMMARY SHEET ── */
  const summaryRows = groupedData.map((loc) => {
    const eRickshaw  = loc.bikes.filter((b) => b.vehicleType === "E_RICKSHAW").length;
    const scooty     = loc.bikes.filter((b) => b.vehicleType === "SCOOTY").length;
    const newBikes   = loc.bikes.filter((b) => b.source === "NEW").length;
    const exBikes    = loc.bikes.filter((b) => b.source === "EXCHANGE").length;
    const inStock    = loc.bikes.filter((b) => b.status === "IN_STOCK").length;
    const sold       = loc.bikes.filter((b) => b.status === "SOLD").length;
     const totalSale  = loc.bikes.reduce((s, b) => s + (b.salePrice || 0), 0);

    return {
      "Location":               loc.location,
      "Total Vehicles":         loc.total,
      "E-Rickshaw":             eRickshaw,
      "Scooty":                 scooty,
      "NEW":                    newBikes,
      "EXCHANGE":               exBikes,
      "IN STOCK":               inStock,
      "SOLD":                   sold,
       "Total Sale Value (₹)":   totalSale,
    };
  });

  // Grand total row
  const gt = summaryRows.reduce(
    (acc, r) => {
      acc["Total Vehicles"]            += r["Total Vehicles"];
      acc["E-Rickshaw"]                += r["E-Rickshaw"];
      acc["Scooty"]                    += r["Scooty"];
      acc["NEW"]                       += r["NEW"];
      acc["EXCHANGE"]                  += r["EXCHANGE"];
      acc["IN STOCK"]                  += r["IN STOCK"];
      acc["SOLD"]                      += r["SOLD"];
      acc["Total Sale Value (₹)"]      += r["Total Sale Value (₹)"];
      return acc;
    },
    {
      "Location": "GRAND TOTAL",
      "Total Vehicles": 0, "E-Rickshaw": 0, "Scooty": 0,
      "NEW": 0, "EXCHANGE": 0, "IN STOCK": 0, "SOLD": 0,
      "Total Sale Value (₹)": 0,
    }
  );
  summaryRows.push(gt);

  const summaryWs = XLSX.utils.json_to_sheet(summaryRows);
  summaryWs["!cols"] = [
    { wch: 16 }, { wch: 14 }, { wch: 12 }, { wch: 10 }, { wch: 10 },
    { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 24 }, { wch: 22 },
  ];
  XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");

  /* ── 4. FILENAME ── */
  const parts = ["EV_Stock"];
  if (filters.vehicleType) parts.push(filters.vehicleType);
  if (filters.location)    parts.push(filters.location);
  if (filters.month)       parts.push(`M${filters.month}`);
  if (filters.year)        parts.push(`Y${filters.year}`);
  if (filters.fromDate)    parts.push(`From-${filters.fromDate}`);
  if (filters.toDate)      parts.push(`To-${filters.toDate}`);
  parts.push(new Date().toISOString().split("T")[0]);

  XLSX.writeFile(wb, `${parts.join("_")}.xlsx`);
};